/**
 * Cross-subdomain preference storage.
 *
 * The portal runs on two hostnames sharing the same Vite bundle:
 *   - `condorsecurity.co`        (public landing)
 *   - `app.condorsecurity.co`    (authenticated app)
 *
 * Each origin has its own `localStorage`, so a theme/lang chosen on one side
 * was lost after a hostname-crossing navigation (e.g. logout bounces app → landing
 * via `useHostnameGuard`). We persist preferences in a cookie scoped to
 * `.condorsecurity.co` so both subdomains see the same value, and keep
 * `localStorage` as a best-effort fallback for dev / first-visit.
 */

const COOKIE_DOMAIN = '.condorsecurity.co';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function hostIsCondor(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location?.hostname;
  if (typeof host !== 'string') return false;
  return host === 'condorsecurity.co' || host.endsWith('.condorsecurity.co');
}

function readCookie(key: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${key}=`;
  const parts = document.cookie.split('; ');
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      const raw = part.slice(prefix.length);
      // A cleared / expired cookie can linger with an empty value — treat as
      // absent so the localStorage fallback kicks in.
      if (raw === '') return null;
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }
  return null;
}

function writeCookie(key: string, value: string): void {
  if (typeof document === 'undefined') return;
  const encoded = encodeURIComponent(value);
  const secure = typeof window !== 'undefined' && window.location?.protocol === 'https:' ? '; Secure' : '';
  const domain = hostIsCondor() ? `; Domain=${COOKIE_DOMAIN}` : '';
  try {
    document.cookie = `${key}=${encoded}; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure}${domain}`;
  } catch {
    // Some test environments stub document in ways that reject assignment —
    // localStorage still captures the write, so callers keep working.
  }
}

export function getSharedPref(key: string): string | null {
  const fromCookie = readCookie(key);
  if (fromCookie !== null) return fromCookie;
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

export function setSharedPref(key: string, value: string): void {
  writeCookie(key, value);
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
  } catch {
    // localStorage might be blocked (private mode) — cookie is already set.
  }
}
